import { z } from "zod";
import { ENTITY_TYPES } from "@/components/filter-cards";
import { MIN_IDENTIFIER_LENGTH, validateIdentifier } from "@/app/(app)/_lib/validate-identifier";
import { validateDob } from "@/app/(app)/_lib/validate-dob";

// DOB: empty (optional filter) OR YYYY-MM-DD / YYYY-MM / YYYY, not in the future.
const dobField = z
  .string()
  .trim()
  .superRefine((v, ctx) => {
    const error = validateDob(v);
    if (error) ctx.addIssue({ code: "custom", message: error });
  });

export const screeningSearchSchema = z
  .object({
    // Name and identifier are both optional individually, but at least one is
    // required (enforced by the `.refine` below) — a search can run by name, by
    // identifier, or both.
    query: z.string().trim(),
    /** Free-text identifier (passport, tax id, etc.) — backend `identifier`. */
    identifier: z
      .string()
      .trim()
      .refine((v) => v === "" || v.length >= MIN_IDENTIFIER_LENGTH, {
        message: `Identifier must be at least ${MIN_IDENTIFIER_LENGTH} characters`,
      })
      .optional(),
    /** 80–99 — the confidence slider's domain (ref app caps at 99). */
    confidenceScore: z.number().int().min(80).max(99),
    entityType: z.enum(ENTITY_TYPES).nullable().optional(),
    dob: dobField.optional(),
    /** Watchlist / data-source ids. Empty = all sanctions lists. */
    watchlists: z.array(z.string()).optional(),
    /** ISO 3166-1 alpha-2 country codes. */
    countries: z.array(z.string()).optional(),
  })
  .refine((v) => v.query.length > 0 || (v.identifier ?? "").length > 0, {
    message: "Enter a name or identifier to screen",
    path: ["query"],
  });

export type ScreeningSearchInput = z.infer<typeof screeningSearchSchema>;

export { validateDob, validateIdentifier };
